import React from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import AddRoundedIcon from '@mui/icons-material/AddRounded'

import { BEIGE_300, BEIGE_600, BOX_SHADOW_MD, GRAY_700, GRAY_900, WHITE } from 'constants/styling/theme'
import { RealEstatePropertyListItemDTO } from 'models/property'
import propertyPlaceholder from 'assets/img/property/property-placeholder.webp'
import { usePropertySearchResults } from './usePropertySearchResults'
import { propertyOptionId } from './usePropertySearchKeyboard'

interface Props {
  /** Current search text */
  searchText: string
  /** Whether the search input is currently focused */
  isSearchFocused: boolean
  /** Callback when a property is selected from search results */
  onSelectProperty: (property: RealEstatePropertyListItemDTO) => void
  /** Row the keyboard has highlighted, or -1. Owned by the drawer, because the
   *  keys are pressed in the search input rather than in this list. */
  activeIndex?: number
  /** Keeps the keyboard highlight in step with the pointer, so the two cannot
   *  disagree about which row Enter would take. */
  onHoverIndex?: (index: number) => void
  /** Opens the create-property form. Offered inside the no-results panel, where
   *  it is the only thing left to do. */
  onCreateNew?: () => void
}

/** The dropdown panel, shared by the results and the no-results state so the two
 *  read as one surface that changes contents rather than two different things. */
const PANEL_SX = {
  position: 'absolute',
  left: 0,
  right: 0,
  top: '100%',
  mt: 0.2,
  backgroundColor: 'white',
  border: `1px solid ${BEIGE_600}`,
  borderRadius: '0.8rem',
  boxShadow: BOX_SHADOW_MD,
  overflow: 'hidden',
  zIndex: 10,
} as const

/** The dropdown's DOM id, so the search input can point `aria-controls` at it. */
export const PROPERTY_LISTBOX_ID = 'pf-property-listbox'

/**
 * Property search results dropdown for the revamped purchase flow drawer.
 * Renders an absolutely-positioned list over the search input.
 */
export const PropertySearchList: React.FC<Props> = ({
  searchText,
  isSearchFocused,
  onSelectProperty,
  activeIndex = -1,
  onHoverIndex,
  onCreateNew,
}) => {
  const { t } = useTranslation(['order', 'purchase_flow_revamp'])

  const { properties: displayedProperties, showResults, showEmpty, showEmptyFocusHint } =
    usePropertySearchResults(searchText, isSearchFocused)

  return (
    <>
      {showEmptyFocusHint &&
        <Typography variant="text-sm" color={GRAY_700} sx={{ px: 0.5, mt: 0.8 }}>
          {t('purchase_flow_revamp:drawer.property_search_empty_hint')}
        </Typography>
      }

      {showEmpty &&
        <Box sx={PANEL_SX}>

          <Typography variant="text-sm" color={GRAY_700} sx={{ display: 'block', p: 1.2 }}>
            {t('purchase_flow_revamp:drawer.property_search_no_match')}
          </Typography>

          {/* The way out, offered where the dead end is. A row rather than a link,
              because it is a command; the input's onBlur closes the dropdown after
              150ms, so it acts on mousedown, before that unmount. */}
          {onCreateNew &&
            <Stack
              component="button"
              type="button"
              direction="row"
              alignItems="center"
              gap={1.2}
              onMouseDown={onCreateNew}
              data-cy="pf-revamp-property-search-create"
              sx={{
                width: '100%',
                p: 1.2,
                border: 'none',
                backgroundColor: WHITE,
                color: GRAY_900,
                font: 'inherit',
                textAlign: 'left',
                cursor: 'pointer',
                '&:hover': { backgroundColor: BEIGE_300 },
              }}
            >
              <AddRoundedIcon sx={{ fontSize: '2rem' }} />
              <Typography variant="text-sm" fontWeight={550} color={GRAY_900}>
                {t('purchase_flow_revamp:drawer.property_search_create')}
              </Typography>
            </Stack>
          }

        </Box>
      }

      {showResults &&
        // Pressing a row must not blur the input: the blur closes the list after 150ms, and a press held
        // longer (a slow tap on a phone) then lands on whatever the list was covering.
        <Box sx={PANEL_SX} onMouseDown={(event) => event.preventDefault()}>
          <Stack spacing={0} p={0.3} id={PROPERTY_LISTBOX_ID} role="listbox" aria-label={t('order:step_address.search_property_placeholder')}>

            {displayedProperties.map((property, index) => (
              <Stack
                key={property.id}
                id={propertyOptionId(index)}
                role="option"
                aria-selected={index === activeIndex}
                direction="row"
                alignItems="center"
                gap={0.8}
                p={0.8}
                sx={{
                  borderRadius: '0.6rem',
                  cursor: 'pointer',
                  // The keyboard highlight paints the same beige the pointer does,
                  // so one row is highlighted at a time however the client got there.
                  backgroundColor: index === activeIndex ? BEIGE_300 : undefined,
                  '&:hover': { backgroundColor: BEIGE_300 },
                }}
                onMouseEnter={() => onHoverIndex?.(index)}
                onClick={() => onSelectProperty(property)}
              >

                <Box
                  component="img"
                  src={propertyPlaceholder}
                  alt=""
                  sx={{ width: '4rem', height: '4rem', borderRadius: '0.4rem', objectFit: 'cover', flexShrink: 0 }}
                />

                <Stack spacing={0} overflow="hidden" flex={1}>

                  <Typography variant="text-sm" fontWeight={500} color={GRAY_900} noWrap>
                    {property.title || property.address?.street || property.address?.postalCode || property.address?.country}
                  </Typography>

                  <Typography variant="text-xs" color={GRAY_700} noWrap>
                    {[
                      `${property.address?.street ?? ''} ${property.address?.streetNumber ?? ''}`.trim(),
                      property.address?.city,
                      property.address?.country,
                    ].filter(Boolean).join(', ')}
                  </Typography>

                </Stack>

              </Stack>
            ))}

          </Stack>
        </Box>
      }
    </>
  )
}
